import React, { useState, useEffect, useCallback } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
    Box, Flex, Text, Button, IconButton, Input, HStack, SimpleGrid,
    Badge, useToast, Table, Thead, Tbody, Tr, Th, Td, Skeleton,
    Avatar, FormControl, FormLabel
} from "@chakra-ui/react";
import {
    ArrowLeft, Trophy, Calendar, RefreshCw, Clock, Target,
    MessageSquare, Phone, Users, ListChecks
} from "lucide-react";
import {
    XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    ResponsiveContainer, AreaChart, Area
} from "recharts";
import {
    getDesempenhoAtendentes, getDesempenhoAtendenteHistorico
} from "../services/api";

const DEP_BADGE = {
    comercial:  { label: "Comercial",  color: "orange" },
    suporte:    { label: "Suporte",    color: "blue" },
    financeiro: { label: "Financeiro", color: "purple" },
};

// Indicadores do gerente de contas (modo CRM), na ordem de exibição.
const CRM_IND_HIST = [
    { codigo: "crm_ligacoes_realizadas", label: "Ligações",  abbr: "Lig" },
    { codigo: "crm_mensagens_whatsapp",  label: "Mensagens", abbr: "Msg" },
    { codigo: "crm_clientes_contatados", label: "Clientes",  abbr: "Cli" },
    { codigo: "crm_tarefas_concluidas",  label: "Tarefas",   abbr: "Tar" },
];

const hojeISO = () => new Date().toISOString().slice(0, 10);
const hojeMinusISO = (dias) => {
    const d = new Date();
    d.setDate(d.getDate() - dias);
    return d.toISOString().slice(0, 10);
};

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        const d = iso.length === 10 ? new Date(iso + "T12:00:00") : new Date(iso);
        return d.toLocaleDateString("pt-BR");
    } catch { return iso; }
};

const formatSeg = (seg) => {
    const v = Number(seg || 0);
    if (v < 60) return `${v.toFixed(0)}s`;
    if (v < 3600) return `${(v / 60).toFixed(1)} min`;
    return `${(v / 3600).toFixed(1)}h`;
};

const notaColor = (nota) => {
    const n = Number(nota || 0);
    if (n >= 85) return "green";
    if (n >= 70) return "blue";
    if (n >= 50) return "yellow";
    return "red";
};

// "realizado/meta" para uma tarefa diária do CRM (ou "—" se não houver).
const crmCellTxt = (t) => t ? `${Math.round(Number(t.valor_realizado || 0))}/${Math.round(Number(t.valor_esperado || 0))}` : "—";

const DesempenhoAtendenteHistorico = () => {
    const { id } = useParams();
    const toast = useToast();

    const [atendente, setAtendente] = useState(null);
    const [loading, setLoading] = useState(true);
    const [inicio, setInicio] = useState(hojeMinusISO(30));
    const [fim, setFim] = useState(hojeISO());
    const [data, setData] = useState({ metricas: [], tarefas: [] });

    const fetchAtendente = useCallback(async () => {
        const res = await getDesempenhoAtendentes({});
        if (res && res.data) {
            const found = res.data.find(a => String(a.id) === String(id));
            setAtendente(found || null);
        }
    }, [id]);

    const fetchHistorico = useCallback(async () => {
        setLoading(true);
        const res = await getDesempenhoAtendenteHistorico(id, { inicio, fim });
        if (res) {
            setData({
                metricas: res.metricas || res.data?.metricas || [],
                tarefas: res.tarefas || res.data?.tarefas || [],
            });
        } else {
            toast({ status: "error", title: "Erro ao carregar histórico" });
        }
        setLoading(false);
    }, [id, inicio, fim, toast]);

    useEffect(() => { fetchAtendente(); }, [fetchAtendente]);
    useEffect(() => { fetchHistorico(); }, [fetchHistorico]);

    const metricasOrdenadas = [...(data.metricas || [])].sort((a, b) => (a.data > b.data ? 1 : -1));
    const tarefasPorData = {};
    (data.tarefas || []).forEach(t => {
        const key = t.data;
        if (!tarefasPorData[key]) tarefasPorData[key] = [];
        tarefasPorData[key].push(t);
    });

    const mediaNota = metricasOrdenadas.length > 0
        ? metricasOrdenadas.reduce((s, m) => s + Number(m.nota_geral || 0), 0) / metricasOrdenadas.length
        : 0;

    const somaContatos = metricasOrdenadas.reduce((s, m) => s + Number(m.contatos_total || 0), 0);
    const somaFechados = metricasOrdenadas.reduce((s, m) => s + Number(m.contatos_fechados || 0), 0);
    const mediaSLA = metricasOrdenadas.length > 0
        ? metricasOrdenadas.reduce((s, m) => s + Number(m.tempo_medio_primeira_resposta_seg || 0), 0) / metricasOrdenadas.filter(m => m.tempo_medio_primeira_resposta_seg).length || 0
        : 0;

    const chartData = metricasOrdenadas.map(m => ({
        date: formatDate(m.data).slice(0, 5),
        nota: Number(m.nota_geral || 0),
        tempo: Math.round(Number(m.tempo_medio_primeira_resposta_seg || 0)),
        contatos: Number(m.contatos_total || 0),
    }));

    const dep = atendente && (DEP_BADGE[atendente.departamento] || { label: atendente.departamento, color: "gray" });
    const ehCrm = atendente?.modo === "crm";

    // Totais dos indicadores do CRM no período (soma do realizado por indicador).
    const crmTotais = {};
    CRM_IND_HIST.forEach(ind => { crmTotais[ind.codigo] = 0; });
    (data.tarefas || []).forEach(t => {
        const c = t.template?.codigo;
        if (c && c in crmTotais) crmTotais[c] += Number(t.valor_realizado || 0);
    });

    const tarefasMap = new Map();
    (data.tarefas || []).forEach(t => {
        const codigo = t.template?.codigo || t.tarefa_template_id;
        if (!tarefasMap.has(codigo)) tarefasMap.set(codigo, t.template);
    });

    return (
        <Box ml={{ base: "0", md: "64px" }} p={{ base: 3, md: 6 }} pt="75px" minH="100vh" bg="gray.50">
            <Box maxW="1600px" mx="auto">
                {/* Header */}
                <Flex align="center" mb={4} gap={3}>
                    <IconButton as={RouterLink} to="/desempenho" icon={<ArrowLeft size={16} />} size="sm" variant="outline" aria-label="Voltar" />
                    <Box flex={1}>
                        <HStack>
                            <Trophy size={20} color="#F68633" />
                            <Text fontSize="xl" fontWeight="bold" color="gray.800">Histórico de Desempenho</Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>
                            Evolução diária da nota, SLA e produtividade nos últimos {Math.round((new Date(fim) - new Date(inicio)) / 86400000)} dias.
                        </Text>
                    </Box>
                    <IconButton icon={<RefreshCw size={16} />} size="sm" variant="outline" onClick={fetchHistorico} aria-label="Atualizar" />
                </Flex>

                {/* Atendente Card */}
                {atendente ? (
                    <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" p={4} mb={4}>
                        <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
                            <HStack>
                                <Avatar size="md" name={atendente.user?.name || atendente.apelido_poli} />
                                <Box>
                                    <Text fontSize="md" fontWeight="bold" color="gray.800">{atendente.user?.name || atendente.apelido_poli || "—"}</Text>
                                    <HStack spacing={2} mt={0.5}>
                                        {dep && <Badge colorScheme={dep.color} fontSize="10px" borderRadius="md">{dep.label}</Badge>}
                                        {ehCrm && <Badge colorScheme="purple" fontSize="10px" borderRadius="md">Gerente de contas</Badge>}
                                        <Text fontSize="xs" color="gray.500">{atendente.user?.email}</Text>
                                    </HStack>
                                    <Text fontSize="10px" color="gray.400" mt={0.5}>
                                        {ehCrm
                                            ? "Avaliado pelas métricas do CRM (ligações, WhatsApp, clientes e tarefas)."
                                            : `POLI ID: ${atendente.poli_user_id} · Apelido: ${atendente.apelido_poli}`}
                                    </Text>
                                </Box>
                            </HStack>
                        </Flex>
                    </Box>
                ) : (
                    <Skeleton height="85px" borderRadius="xl" mb={4} />
                )}

                {/* Filtros */}
                <Flex align="flex-end" mb={4} flexWrap="wrap" gap={3} bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" p={4}>
                    <FormControl maxW="160px">
                        <FormLabel fontSize="10px" color="gray.500" fontWeight="semibold" mb={1} textTransform="uppercase">Início</FormLabel>
                        <Input size="sm" type="date" borderRadius="md" value={inicio} onChange={(e) => setInicio(e.target.value)} />
                    </FormControl>
                    <FormControl maxW="160px">
                        <FormLabel fontSize="10px" color="gray.500" fontWeight="semibold" mb={1} textTransform="uppercase">Fim</FormLabel>
                        <Input size="sm" type="date" borderRadius="md" value={fim} onChange={(e) => setFim(e.target.value)} />
                    </FormControl>
                    <HStack>
                        {[7, 15, 30, 60, 90].map(d => (
                            <Button key={d} size="xs" variant="outline" onClick={() => { setInicio(hojeMinusISO(d)); setFim(hojeISO()); }}>
                                {d}d
                            </Button>
                        ))}
                    </HStack>
                    <Button size="sm" ml="auto" leftIcon={<MessageSquare size={14} />} variant="outline" as={RouterLink} to={`/desempenho?tab=avaliacoes&atendente_id=${id}`}>
                        Ver Avaliações IA
                    </Button>
                </Flex>

                {/* Cards resumo */}
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={4}>
                    <SummaryCard label="Nota Média" value={mediaNota.toFixed(1)} icon={Trophy} color={notaColor(mediaNota)} loading={loading} />
                    {ehCrm ? (<>
                        <SummaryCard label="Ligações (total)" value={Math.round(crmTotais.crm_ligacoes_realizadas)} icon={Phone} color="blue" loading={loading} />
                        <SummaryCard label="Mensagens (total)" value={Math.round(crmTotais.crm_mensagens_whatsapp)} icon={MessageSquare} color="green" loading={loading} />
                        <SummaryCard label="Tarefas feitas" value={Math.round(crmTotais.crm_tarefas_concluidas)} icon={ListChecks} color="purple" loading={loading} />
                    </>) : (<>
                        <SummaryCard label="Contatos (total)" value={somaContatos} icon={MessageSquare} color="blue" loading={loading} />
                        <SummaryCard label="Contatos Fechados" value={somaFechados} icon={Target} color="green" loading={loading} />
                        <SummaryCard label="Tempo Médio Resp." value={formatSeg(mediaSLA)} icon={Clock} color="purple" loading={loading} isText />
                    </>)}
                </SimpleGrid>

                {/* Gráfico evolução */}
                <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" p={4} mb={4}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>Evolução da Nota Diária</Text>
                    {loading ? (
                        <Skeleton height="260px" />
                    ) : chartData.length === 0 ? (
                        <Box py={8} textAlign="center">
                            <Text fontSize="sm" color="gray.400">Sem dados no período selecionado.</Text>
                        </Box>
                    ) : (
                        <Box h="260px">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="date" fontSize={11} stroke="#94a3b8" />
                                    <YAxis domain={[0, 100]} fontSize={11} stroke="#94a3b8" />
                                    <RechartsTooltip />
                                    <Area type="monotone" dataKey="nota" stroke="#F68633" fill="#fed7aa" strokeWidth={2} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Box>
                    )}
                </Box>

                {/* Tabela tarefas diárias */}
                <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden">
                    <Flex px={4} py={3} borderBottom="1px solid" borderColor="gray.100" align="center" justify="space-between">
                        <HStack>
                            <Calendar size={16} color="#F68633" />
                            <Text fontSize="sm" fontWeight="bold" color="gray.700">Métricas e Tarefas Diárias</Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.500">{metricasOrdenadas.length} dia(s)</Text>
                    </Flex>
                    {loading ? (
                        <Box p={4}><Skeleton height="40px" mb={2} /><Skeleton height="40px" mb={2} /><Skeleton height="40px" /></Box>
                    ) : metricasOrdenadas.length === 0 ? (
                        <Text textAlign="center" py={10} color="gray.400" fontSize="sm">Nenhum dado no período</Text>
                    ) : (
                        <Box overflowX="auto">
                            <Table size="sm" variant="simple">
                                <Thead bg="gray.50">
                                    <Tr>
                                        <Th fontSize="xs">Data</Th>
                                        <Th fontSize="xs" isNumeric>Nota</Th>
                                        {ehCrm ? (<>
                                            <Th fontSize="xs" isNumeric>Ligações</Th>
                                            <Th fontSize="xs" isNumeric>Mensagens</Th>
                                            <Th fontSize="xs" isNumeric>Clientes</Th>
                                            <Th fontSize="xs" isNumeric>Tarefas feitas</Th>
                                        </>) : (<>
                                            <Th fontSize="xs" isNumeric>Contatos</Th>
                                            <Th fontSize="xs" isNumeric>Fechados</Th>
                                            <Th fontSize="xs" isNumeric>T. Resp</Th>
                                            <Th fontSize="xs" isNumeric>Dentro SLA</Th>
                                            <Th fontSize="xs" isNumeric>Fora SLA</Th>
                                            <Th fontSize="xs" isNumeric>ML</Th>
                                            <Th fontSize="xs">Tarefas</Th>
                                        </>)}
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {[...metricasOrdenadas].reverse().map((m) => {
                                        const tarefas = tarefasPorData[m.data] || [];
                                        const tByCodigo = {};
                                        tarefas.forEach(t => { if (t.template?.codigo) tByCodigo[t.template.codigo] = t; });
                                        return (
                                            <Tr key={m.id || m.data} _hover={{ bg: "gray.50" }}>
                                                <Td fontSize="xs" color="gray.700" fontWeight="semibold">{formatDate(m.data)}</Td>
                                                <Td isNumeric>
                                                    {m.nota_geral != null ? (
                                                        <Badge colorScheme={notaColor(m.nota_geral)} fontSize="11px" borderRadius="md" px={2}>
                                                            {Number(m.nota_geral).toFixed(1)}
                                                        </Badge>
                                                    ) : <Text fontSize="xs" color="gray.400">—</Text>}
                                                </Td>
                                                {ehCrm ? (<>
                                                    <Td isNumeric fontSize="xs" color="gray.700">{crmCellTxt(tByCodigo.crm_ligacoes_realizadas)}</Td>
                                                    <Td isNumeric fontSize="xs" color="gray.700">{crmCellTxt(tByCodigo.crm_mensagens_whatsapp)}</Td>
                                                    <Td isNumeric fontSize="xs" color="gray.700">{crmCellTxt(tByCodigo.crm_clientes_contatados)}</Td>
                                                    <Td isNumeric fontSize="xs" color="gray.700">{crmCellTxt(tByCodigo.crm_tarefas_concluidas)}</Td>
                                                </>) : (<>
                                                    <Td isNumeric fontSize="xs" color="gray.700">{m.contatos_total ?? 0}</Td>
                                                    <Td isNumeric fontSize="xs" color="gray.700">{m.contatos_fechados ?? 0}</Td>
                                                    <Td isNumeric fontSize="xs" color="gray.700">{m.tempo_medio_primeira_resposta_seg ? formatSeg(m.tempo_medio_primeira_resposta_seg) : "—"}</Td>
                                                    <Td isNumeric fontSize="xs" color="green.600">{m.mensagens_respondidas_dentro_sla ?? 0}</Td>
                                                    <Td isNumeric fontSize="xs" color="red.500">{m.mensagens_respondidas_fora_sla ?? 0}</Td>
                                                    <Td isNumeric fontSize="xs" color="gray.700">{m.perguntas_ml_respondidas ?? 0}</Td>
                                                    <Td>
                                                        <HStack spacing={1} flexWrap="wrap">
                                                            {tarefas.map(t => (
                                                                <Badge key={t.id} colorScheme={notaColor(t.nota)} fontSize="9px" borderRadius="md" px={1.5} title={t.template?.nome}>
                                                                    {t.template?.codigo || t.tipo}: {Number(t.nota || 0).toFixed(0)}
                                                                </Badge>
                                                            ))}
                                                        </HStack>
                                                    </Td>
                                                </>)}
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

const SummaryCard = ({ label, value, icon: Icon, color, loading, isText }) => (
    <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" p={4}>
        <Flex align="center" gap={3}>
            <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg={`${color}.50`}>
                <Icon size={20} color={`var(--chakra-colors-${color}-500)`} />
            </Flex>
            <Box flex={1} minW={0}>
                <Text fontSize="10px" color="gray.500" fontWeight="semibold" textTransform="uppercase">{label}</Text>
                {loading ? <Skeleton height="24px" w="70%" mt={1} /> : (
                    <Text fontSize={isText ? "md" : "xl"} fontWeight="bold" color="gray.800">{value}</Text>
                )}
            </Box>
        </Flex>
    </Box>
);

export default DesempenhoAtendenteHistorico;
